import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PageContainer,
  PageHeader,
  PillTabs,
  IconButton,
  List,
  ListRow,
  EmptyState,
  LoadingState,
  Modal,
  TimeStamp,
} from '../../components/layout';
import { agentApi } from '../../api/agents';
import { conversationApi } from '../../api/conversations';
import type { AgentSummary, Conversation, ConversationParticipantInfo } from '../../types';
import { useAgentStore, useConversationStore, toast } from '../../store';
import { extractErrorMessage } from '../../utils/error';

type ConvMode = 'human-bot' | 'bot-bot';

// ─── Helpers ──────────────────────────────────────────────────────────────

/**
 * 40×40 avatar slot for a conversation row. Direct chats render the
 * single counterpart's image as a rounded square; group chats render
 * up to 4 participant avatars in a 2×2 mosaic — the WeChat group
 * avatar pattern.
 */
function ConvAvatar({ participants }: { participants: ConversationParticipantInfo[] }) {
  if (participants.length === 0) {
    return <div className="w-10 h-10 rounded-md bg-gray-100 dark:bg-white/[0.05]" />;
  }
  if (participants.length === 1) {
    const p = participants[0];
    return (
      <div className="w-10 h-10 rounded-md overflow-hidden bg-gray-100 dark:bg-white/[0.05]">
        {p.agentAvatar ? (
          <img src={p.agentAvatar} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/25 to-primary/5">
            <span className="text-primary text-sm font-bold">{p.agentName.charAt(0)}</span>
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="w-10 h-10 rounded-md overflow-hidden bg-gray-100 dark:bg-white/[0.05] grid grid-cols-2 gap-px p-px">
      {participants.slice(0, 4).map((p, i) => (
        <div key={i} className="bg-gradient-to-br from-primary/25 to-primary/5 flex items-center justify-center">
          {p.agentAvatar ? (
            <img src={p.agentAvatar} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-primary text-[9px] font-bold">{p.agentName.charAt(0)}</span>
          )}
        </div>
      ))}
    </div>
  );
}

/** WeChat-style relative timestamp: "Nm" / "Nh" / "Yesterday" / "Mon DD". */
function formatConvTime(dateStr: string): string {
  const date = new Date(dateStr);
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `${diffMins}m`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h`;
  if (diffHours < 48) return 'Yesterday';
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/** Resolve the participants and display title for a conversation
 * relative to the current user's primary agent. Direct chats hide
 * the user's own agent from the participant list. */
function resolveConvDisplay(conv: Conversation, myAgentId?: string) {
  const all = conv.participants ?? [];
  const visible =
    conv.type === 'direct' && myAgentId
      ? all.filter((p) => p.agentId !== myAgentId)
      : all;
  const title =
    conv.type === 'group'
      ? conv.title || 'Group'
      : visible.map((p) => p.agentName).join(', ') || 'Direct';
  return { visible, title };
}

export function ConversationListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { conversations, loading, fetchConversations } = useConversationStore();
  const { myAgents, primaryAgentId, fetchMyAgents } = useAgentStore();
  const [mode, setMode] = useState<ConvMode>('human-bot');
  const [createOpen, setCreateOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [participantIds, setParticipantIds] = useState<string[]>([]);
  const [candidateAgents, setCandidateAgents] = useState<AgentSummary[]>([]);
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const myAgentId = primaryAgentId || myAgents[0]?.id;

  // ─── Default group: a permanent, undeletable conversation that
  // gathers ALL of the user's owned agents. This is where voice
  // commands from the FAB get broadcast — the "总群" the user can
  // always fall back on. Synthesized client-side from `myAgents`
  // (no backend roundtrip) and pinned to the very top of human-bot
  // mode. The detail page recognizes the well-known id `default-team`
  // and renders the same way without an API fetch. */
  const DEFAULT_TEAM_ID = 'default-team';
  const defaultTeamConversation = useMemo<Conversation | null>(() => {
    if (mode !== 'human-bot' || myAgents.length === 0) return null;
    const participants: ConversationParticipantInfo[] = myAgents.map((a) => ({
      agentId:      a.id,
      agentName:    a.name,
      agentAvatar:  a.avatar,
      role:         a.id === myAgentId ? 'creator' : 'member',
    }));
    return {
      id:                  DEFAULT_TEAM_ID,
      type:                'group',
      chatType:            'human-bot',
      title:               t('conversations.defaultTeam', '我和我的 Agents'),
      creatorAgentId:      myAgentId,
      participantCount:    myAgents.length,
      messageCount:        0,
      lastMessagePreview:  t('conversations.defaultTeamHint', '默认群 · 语音指令默认分发到这里'),
      lastMessageAt:       new Date().toISOString(),
      status:              'active',
      createdAt:           new Date().toISOString(),
      participants,
    } as unknown as Conversation;
  }, [mode, myAgents, myAgentId, t]);

  useEffect(() => {
    fetchMyAgents();
  }, [fetchMyAgents]);

  useEffect(() => {
    const refresh = () => {
      void fetchConversations(mode, myAgentId);
    };
    refresh();
    const interval = setInterval(() => {
      if (!document.hidden) refresh();
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchConversations, myAgentId, mode]);

  const availableCandidates = useMemo(
    () => candidateAgents.filter((a) => a.id !== myAgentId && a.status !== 'dead'),
    [candidateAgents, myAgentId],
  );

  const toggleParticipant = (agentId: string) => {
    setParticipantIds((prev) => (
      prev.includes(agentId)
        ? prev.filter((id) => id !== agentId)
        : [...prev, agentId]
    ));
  };

  const openCreateDialog = async () => {
    setCreateOpen(true);
    if (candidateAgents.length > 0 || candidateLoading) return;

    setCandidateLoading(true);
    try {
      const res = await agentApi.getAgentList(1, 100);
      setCandidateAgents(res.items || []);
    } catch (error) {
      toast.error(extractErrorMessage(error, t('conversations.loadAgentsFailed', 'Failed to load agents')));
    } finally {
      setCandidateLoading(false);
    }
  };

  const closeCreateDialog = () => {
    if (creating) return;
    setCreateOpen(false);
    setTitle('');
    setParticipantIds([]);
  };

  const createConversation = async () => {
    if (participantIds.length < 1) {
      toast.error(t('conversations.participantRequired', 'Select at least 1 participant'));
      return;
    }

    const isGroup = participantIds.length > 1;
    const groupTitle = title.trim();
    if (isGroup && !groupTitle) {
      toast.error(t('conversations.groupNameRequired', 'Group name is required'));
      return;
    }

    setCreating(true);
    try {
      const out = await conversationApi.createConversation(
        isGroup ? groupTitle : '',
        participantIds,
        myAgentId,
      );
      toast.success(
        isGroup
          ? t('conversations.groupCreateSuccess', 'Group created')
          : t('conversations.directCreateSuccess', 'Conversation created'),
      );
      closeCreateDialog();
      await fetchConversations('human-bot', myAgentId);
      navigate(`/conversations/${out.conversationId}`);
    } catch (error) {
      toast.error(
        extractErrorMessage(
          error,
          isGroup
            ? t('conversations.groupCreateFailed', 'Failed to create group')
            : t('conversations.directCreateFailed', 'Failed to create conversation'),
        ),
      );
    } finally {
      setCreating(false);
    }
  };

  // Mode tab options for the inset PillTabs in the page header.
  const modeOptions = [
    { value: 'human-bot' as ConvMode, label: t('conversations.messages',    'Messages'),     icon: 'chat'  },
    { value: 'bot-bot'   as ConvMode, label: t('conversations.agentSocial', 'Agent Social'), icon: 'forum' },
  ];

  return (
    <PageContainer
      width="list"
      header={
        <PageHeader
          title={t('conversations.title')}
          actions={
            <IconButton
              icon="group_add"
              label={t('conversations.createConversation', 'New conversation')}
              onClick={() => void openCreateDialog()}
            />
          }
        >
          <PillTabs
            variant="inset"
            value={mode}
            onChange={setMode}
            options={modeOptions}
          />
        </PageHeader>
      }
    >
      {loading && conversations.length === 0 && !defaultTeamConversation ? (
        <LoadingState />
      ) : conversations.length === 0 && !defaultTeamConversation ? (
        <EmptyState
          icon="forum"
          title={t('conversations.empty')}
          description={t('conversations.emptyHint')}
        />
      ) : (
        <List>
          {/* Pinned default-team row — stays at the top regardless of
              the real conversations' last-message-at sort. Renders a
              tiny "默认" pill so the user knows this row is special
              and undeletable. */}
          {defaultTeamConversation && (
            <ListRow
              key={DEFAULT_TEAM_ID}
              avatar={<ConvAvatar participants={defaultTeamConversation.participants ?? []} />}
              title={
                <span className="inline-flex items-center gap-1.5 min-w-0">
                  <span className="truncate font-semibold text-gray-900 dark:text-gray-100">
                    {defaultTeamConversation.title}
                  </span>
                  <span className="flex-shrink-0 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-primary/15 text-primary">
                    {t('conversations.defaultBadge', '默认')}
                  </span>
                </span>
              }
              subtitle={defaultTeamConversation.lastMessagePreview || ''}
              meta={<TimeStamp>{t('conversations.always', '常驻')}</TimeStamp>}
              onClick={() => navigate(`/conversations/${DEFAULT_TEAM_ID}`)}
              // Pinned-row emphasis: a soft tinted fill that's a bit
              // deeper than the page background so this row reads as
              // "always here, always selectable" without being noisy.
              className="bg-gray-100 dark:bg-white/[0.05] hover:bg-gray-200 dark:hover:bg-white/[0.07]"
            />
          )}
          {conversations
            .filter((conv) => conv.id !== DEFAULT_TEAM_ID)
            .map((conv) => {
              const { visible, title } = resolveConvDisplay(conv, myAgentId);
              const unread = conv.unreadCount ?? 0;
              return (
                <ListRow
                  key={conv.id}
                  avatar={<ConvAvatar participants={visible} />}
                  title={title}
                  subtitle={conv.lastMessagePreview || ''}
                  meta={
                    <>
                      {conv.lastMessageAt && <TimeStamp>{formatConvTime(conv.lastMessageAt)}</TimeStamp>}
                      {unread > 0 && (
                        <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold text-white bg-red-500 rounded-full">
                          {unread > 99 ? '99+' : unread}
                        </span>
                      )}
                    </>
                  }
                  onClick={() => navigate(`/conversations/${conv.id}`)}
                />
              );
            })}
        </List>
      )}

      {/* New-conversation dialog — uses the layout `<Modal>` primitive
          so it inherits the project's two-color surface, hairline
          dividers, sticky footer and dismiss/escape behavior. */}
      <Modal
        open={createOpen}
        onOpenChange={(open) => {
          if (!open) closeCreateDialog();
          else setCreateOpen(true);
        }}
        title={t('conversations.createConversation', 'New conversation')}
        size="md"
        dismissible={!creating}
        footer={
          <>
            <button
              onClick={closeCreateDialog}
              disabled={creating}
              className="px-4 py-1.5 rounded-full text-xs font-medium border border-gray-200 dark:border-white/[0.12] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.04] disabled:opacity-50 transition-colors"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              onClick={() => void createConversation()}
              disabled={creating || participantIds.length === 0 || (participantIds.length > 1 && title.trim().length === 0)}
              className="px-4 py-1.5 rounded-full text-xs font-medium bg-primary text-white hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {creating ? t('common.loading', 'Loading...') : t('common.create', 'Create')}
            </button>
          </>
        }
      >
        <div className="p-4 space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                {t('conversations.selectParticipants', 'Select participants')}
              </label>
              <span className="text-[11px] text-gray-400">
                {participantIds.length} / 1+
              </span>
            </div>
            <p className="text-[11px] text-gray-400 mb-2">
              {participantIds.length <= 1
                ? t('conversations.directHint', 'Select 1 participant to start a direct chat')
                : t('conversations.groupHint', '2+ participants will create a group chat')}
            </p>
            <div className="max-h-56 overflow-y-auto rounded border border-gray-100 dark:border-white/[0.06] divide-y divide-gray-100 dark:divide-white/[0.06]">
              {candidateLoading ? (
                <div className="py-6 text-center text-xs text-gray-400">{t('common.loading', 'Loading...')}</div>
              ) : availableCandidates.length === 0 ? (
                <div className="py-6 text-center text-xs text-gray-400">{t('conversations.noCandidates', 'No available agents')}</div>
              ) : (
                availableCandidates.map((agent) => (
                  <label
                    key={agent.id}
                    className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-white/[0.03]"
                  >
                    <input
                      type="checkbox"
                      checked={participantIds.includes(agent.id)}
                      disabled={creating}
                      onChange={() => toggleParticipant(agent.id)}
                      className="accent-primary"
                    />
                    <img src={agent.avatar} alt="" className="w-8 h-8 rounded-md object-cover bg-gray-100 dark:bg-white/[0.05]" />
                    <div className="min-w-0">
                      <p className="text-sm text-gray-900 dark:text-gray-100 truncate">{agent.name}</p>
                      <p className="text-[11px] text-gray-400 truncate">{agent.creatorName}</p>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>

          {participantIds.length > 1 && (
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                {t('conversations.groupName', 'Group name')}
              </label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={creating}
                placeholder={t('conversations.groupNamePlaceholder', 'Enter a group name')}
                className="w-full rounded border border-gray-100 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.04] px-3 py-2 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-1 focus:ring-primary/40"
              />
            </div>
          )}
        </div>
      </Modal>
    </PageContainer>
  );
}
